import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent,
  type TouchEvent,
} from 'react';

const MIN_SCALE = 0.5;
const MAX_SCALE = 2;
const ZOOM_STEP = 0.1;
const DRAG_THRESHOLD = 6;

interface PanState {
  x: number;
  y: number;
  scale: number;
}

interface TouchPoint {
  clientX: number;
  clientY: number;
}

interface TouchPointList {
  length: number;
  [index: number]: TouchPoint;
}

interface PointerSession {
  active: boolean;
  panning: boolean;
  startX: number;
  startY: number;
  panX: number;
  panY: number;
  clickTarget: HTMLElement | null;
}

function getTouchDistance(touches: TouchPointList): number {
  if (touches.length < 2) return 0;
  const [a, b] = [touches[0], touches[1]];
  const dx = a.clientX - b.clientX;
  const dy = a.clientY - b.clientY;
  return Math.hypot(dx, dy);
}

function getTouchCenter(touches: TouchPointList): { x: number; y: number } {
  if (touches.length < 2) {
    return { x: touches[0].clientX, y: touches[0].clientY };
  }
  return {
    x: (touches[0].clientX + touches[1].clientX) / 2,
    y: (touches[0].clientY + touches[1].clientY) / 2,
  };
}

function measureContentSize(content: HTMLElement, scale: number): { width: number; height: number } {
  const safeScale = scale || 1;
  const bracketRows = content.querySelectorAll<HTMLElement>('[data-bracket-row]');

  if (bracketRows.length > 0) {
    let width = 0;
    bracketRows.forEach((row) => {
      width = Math.max(width, row.getBoundingClientRect().width / safeScale);
    });
    return {
      width,
      height: content.getBoundingClientRect().height / safeScale,
    };
  }

  const rect = content.getBoundingClientRect();
  return {
    width: rect.width / safeScale,
    height: rect.height / safeScale,
  };
}

export function useBracketPan() {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [containerNode, setContainerNode] = useState<HTMLDivElement | null>(null);
  const [pan, setPan] = useState<PanState>({ x: 0, y: 0, scale: 1 });
  const panRef = useRef(pan);
  const isDraggingRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const pointerSession = useRef<PointerSession | null>(null);
  const pinchStart = useRef<{
    distance: number;
    scale: number;
    x: number;
    y: number;
    panX: number;
    panY: number;
  } | null>(null);

  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node;
    setContainerNode(node);
  }, []);

  useEffect(() => {
    panRef.current = pan;
  }, [pan]);

  const clampPan = useCallback((x: number, y: number, scale: number) => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return { x, y };

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const { width: rawWidth, height: rawHeight } = measureContentSize(content, scale);
    const contentWidth = rawWidth * scale;
    const contentHeight = rawHeight * scale;
    const padding = 48;

    const minX =
      contentWidth <= containerWidth
        ? (containerWidth - contentWidth) / 2
        : containerWidth - contentWidth - padding;
    const maxX =
      contentWidth <= containerWidth
        ? (containerWidth - contentWidth) / 2
        : padding;
    const minY =
      contentHeight <= containerHeight
        ? (containerHeight - contentHeight) / 2
        : containerHeight - contentHeight - padding;
    const maxY =
      contentHeight <= containerHeight
        ? (containerHeight - contentHeight) / 2
        : padding;

    return {
      x: Math.max(minX, Math.min(maxX, x)),
      y: Math.max(minY, Math.min(maxY, y)),
    };
  }, []);

  const applyPan = useCallback(
    (next: PanState) => {
      const clamped = clampPan(next.x, next.y, next.scale);
      setPan({
        x: clamped.x,
        y: clamped.y,
        scale: Math.min(MAX_SCALE, Math.max(MIN_SCALE, next.scale)),
      });
    },
    [clampPan],
  );

  const zoomAtPoint = useCallback(
    (delta: number, clientX: number, clientY: number) => {
      const container = containerRef.current;
      if (!container) return;

      setPan((prev) => {
        const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev.scale + delta));
        const scaleRatio = newScale / prev.scale;
        const rect = container.getBoundingClientRect();
        const pointerX = clientX - rect.left;
        const pointerY = clientY - rect.top;
        const newX = pointerX - (pointerX - prev.x) * scaleRatio;
        const newY = pointerY - (pointerY - prev.y) * scaleRatio;
        const clamped = clampPan(newX, newY, newScale);
        return { x: clamped.x, y: clamped.y, scale: newScale };
      });
    },
    [clampPan],
  );

  const onPointerDown = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;

    const clickTarget = (event.target as HTMLElement).closest(
      '[data-bracket-interactive]',
    ) as HTMLElement | null;

    pointerSession.current = {
      active: true,
      panning: false,
      startX: event.clientX,
      startY: event.clientY,
      panX: panRef.current.x,
      panY: panRef.current.y,
      clickTarget,
    };

    if (clickTarget) {
      event.preventDefault();
    }

    event.currentTarget.setPointerCapture(event.pointerId);
  }, []);

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const session = pointerSession.current;
      if (!session?.active) return;

      const dx = event.clientX - session.startX;
      const dy = event.clientY - session.startY;

      if (!session.panning && Math.hypot(dx, dy) >= DRAG_THRESHOLD) {
        session.panning = true;
        session.clickTarget = null;
        isDraggingRef.current = true;
        setIsDragging(true);
      }

      if (!session.panning) return;

      applyPan({
        x: session.panX + dx,
        y: session.panY + dy,
        scale: panRef.current.scale,
      });
    },
    [applyPan],
  );

  const endDrag = useCallback((event: PointerEvent<HTMLDivElement>) => {
    const session = pointerSession.current;

    if (session?.active && !session.panning && session.clickTarget) {
      session.clickTarget.click();
    }

    pointerSession.current = null;
    isDraggingRef.current = false;
    setIsDragging(false);

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const onTouchStart = useCallback((event: TouchEvent<HTMLDivElement>) => {
    if (event.touches.length === 2) {
      const center = getTouchCenter(event.touches);
      pinchStart.current = {
        distance: getTouchDistance(event.touches),
        scale: panRef.current.scale,
        x: center.x,
        y: center.y,
        panX: panRef.current.x,
        panY: panRef.current.y,
      };
    }
  }, []);

  const onTouchEnd = useCallback(() => {
    pinchStart.current = null;
  }, []);

  const zoomIn = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    zoomAtPoint(ZOOM_STEP, rect.left + rect.width / 2, rect.top + rect.height / 2);
  }, [zoomAtPoint]);

  const zoomOut = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    zoomAtPoint(-ZOOM_STEP, rect.left + rect.width / 2, rect.top + rect.height / 2);
  }, [zoomAtPoint]);

  const reset = useCallback(() => {
    applyPan({ x: 0, y: 0, scale: 1 });
  }, [applyPan]);

  const fit = useCallback(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const { width: contentWidth, height: contentHeight } = measureContentSize(content, 1);
    if (contentWidth === 0 || contentHeight === 0) return;

    const padding = 32;
    const scaleX = (containerWidth - padding) / contentWidth;
    const scaleY = (containerHeight - padding) / contentHeight;
    const scale = Math.min(Math.max(Math.min(scaleX, scaleY), MIN_SCALE), MAX_SCALE, 1);
    const x = (containerWidth - contentWidth * scale) / 2;
    const y = (containerHeight - contentHeight * scale) / 2;

    applyPan({ x, y, scale });
  }, [applyPan]);

  useEffect(() => {
    if (!containerNode) return;

    const handleWheel = (event: globalThis.WheelEvent) => {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        const delta = event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
        zoomAtPoint(delta, event.clientX, event.clientY);
        return;
      }

      event.preventDefault();
      const deltaX = event.deltaX !== 0 ? event.deltaX : event.shiftKey ? event.deltaY : 0;
      const deltaY = event.shiftKey ? 0 : event.deltaY;
      applyPan({
        x: panRef.current.x - deltaX,
        y: panRef.current.y - deltaY,
        scale: panRef.current.scale,
      });
    };

    const handleTouchMove = (event: globalThis.TouchEvent) => {
      if (event.touches.length !== 2 || !pinchStart.current) return;
      event.preventDefault();

      const distance = getTouchDistance(event.touches);
      if (distance <= 0 || pinchStart.current.distance <= 0) return;

      const center = getTouchCenter(event.touches);
      const scaleFactor = distance / pinchStart.current.distance;
      const newScale = Math.min(
        MAX_SCALE,
        Math.max(MIN_SCALE, pinchStart.current.scale * scaleFactor),
      );
      const scaleRatio = newScale / pinchStart.current.scale;
      const rect = containerNode.getBoundingClientRect();
      const pointerX = center.x - rect.left;
      const pointerY = center.y - rect.top;
      const newX = pointerX - (pointerX - pinchStart.current.panX) * scaleRatio;
      const newY = pointerY - (pointerY - pinchStart.current.panY) * scaleRatio;

      applyPan({ x: newX, y: newY, scale: newScale });
    };

    containerNode.addEventListener('wheel', handleWheel, { passive: false });
    containerNode.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      containerNode.removeEventListener('wheel', handleWheel);
      containerNode.removeEventListener('touchmove', handleTouchMove);
    };
  }, [applyPan, containerNode, zoomAtPoint]);

  const innerStyle: CSSProperties = {
    transform: `translate(${pan.x}px, ${pan.y}px) scale(${pan.scale})`,
    transformOrigin: 'top left',
    transition: isDragging ? 'none' : 'transform 200ms ease-out',
  };

  return {
    containerRef: setContainerRef,
    contentRef,
    innerStyle,
    scale: pan.scale,
    isDragging,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onTouchStart,
      onTouchEnd,
    },
    zoomIn,
    zoomOut,
    reset,
    fit,
  };
}
